import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Card, Row, Col, Table, Badge, Container, Form, Button, Pagination, InputGroup, Modal, Spinner, Alert } from 'react-bootstrap'
import { supabase } from '../services/supabase'
import Layout from '../components/Layout'
import ProtectedRoute from '../components/ProtectedRoute'

const BUCKET = 'session-attachments'
const WEEKDAYS_FR = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']

// ── Helpers d'affichage : une session saisie à la main utilise ses propres champs ──
const hhmm = (t) => (t ? String(t).substring(0, 5) : '')
const getSessionTitle = (s) => ((s.is_manual && s.title) ? s.title : (s.course_types?.name || s.title || 'Session sans cours'))
const getSessionLocation = (s) => ((s.is_manual && s.location) ? s.location : (s.course_types?.location || s.location || 'Non spécifié'))
const getSportName = (s) => (s.course_types?.name || s.title || 'Autre')
const getSessionTimes = (s) => {
  if (s.is_manual && s.date) {
    const d = new Date(s.date)
    const start = `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
    return { start, end: s.end_time ? hhmm(s.end_time) : '' }
  }
  if (s.course_types?.start_time) return { start: hhmm(s.course_types.start_time), end: hhmm(s.course_types.end_time) }
  return null
}
const getSessionTimeLabel = (s) => {
  const t = getSessionTimes(s)
  if (!t) return 'N/A'
  return t.end ? `${t.start} - ${t.end}` : t.start
}
// Retrouve le cours du programme correspondant à un titre (pour les statistiques par sport)
const findLinkedCourse = (courses, title, weekday) => {
  const n = (title || '').trim().toLowerCase()
  if (!n) return null
  const same = courses.filter(c => (c.name || '').trim().toLowerCase() === n)
  return (
    same.find(c => c.is_active !== false && c.day_of_week === weekday) ||
    same.find(c => c.is_active !== false) ||
    same.find(c => c.day_of_week === weekday) ||
    same[0] || null
  )
}

function Reports() {
  const [stats, setStats] = useState({ sessionsCount: 0, participantsCount: 0, presencesCount: 0, participationRate: 0 })
  const [allSessions, setAllSessions] = useState([])
  const [filteredSessions, setFilteredSessions] = useState([])
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [dateFilter, setDateFilter] = useState('')

  // ── Création manuelle d'une session (saisie libre) ──
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createCourses, setCreateCourses] = useState([])
  const [createTitle, setCreateTitle] = useState('')
  const [createDate, setCreateDate] = useState('')
  const [createStartTime, setCreateStartTime] = useState('')
  const [createEndTime, setCreateEndTime] = useState('')
  const [createLocation, setCreateLocation] = useState('Street Sport Luxembourg')
  const [createError, setCreateError] = useState('')
  const [createLoading, setCreateLoading] = useState(false)
  const [monthlyStats, setMonthlyStats] = useState([])
  const sessionsPerPage = 5

  const [attachmentCounts, setAttachmentCounts] = useState({})

  const [showPresenceModal, setShowPresenceModal] = useState(false)
  const [presenceSession, setPresenceSession] = useState(null)
  const [memberPresences, setMemberPresences] = useState([])
  const [coachPresences, setCoachPresences] = useState([])
  const [presenceLoading, setPresenceLoading] = useState(false)
  // ── Modification manuelle de la liste de présence ──
  const [presenceEditMode, setPresenceEditMode] = useState(false)
  const [presenceDirty, setPresenceDirty] = useState(false)
  const [addSearch, setAddSearch] = useState('')
  const [addResults, setAddResults] = useState([])
  const [addSearching, setAddSearching] = useState(false)
  const [presenceActionId, setPresenceActionId] = useState(null)
  const [presenceMsg, setPresenceMsg] = useState(null)

  const [showAttachmentModal, setShowAttachmentModal] = useState(false)
  const [attachmentSession, setAttachmentSession] = useState(null)
  const [attachmentList, setAttachmentList] = useState([])
  const [attachmentUploading, setAttachmentUploading] = useState(false)
  const [attachmentLoading, setAttachmentLoading] = useState(false)
  const [attachmentError, setAttachmentError] = useState('')
  const [deleteConfirmIdx, setDeleteConfirmIdx] = useState(null)
  const fileInputRef = useRef(null)

  const [showMonthSportModal, setShowMonthSportModal] = useState(false)
  const [monthSportTitle, setMonthSportTitle] = useState('')
  const [monthSportSessions, setMonthSportSessions] = useState([])
  const [monthSportPresences, setMonthSportPresences] = useState({})
  const [monthSportLoading, setMonthSportLoading] = useState(false)

  const loadAttachments = useCallback(async (sessionId) => {
    setAttachmentLoading(true)
    setAttachmentError('')
    try {
      const { data, error } = await supabase
        .from('session_attachments')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true })
      if (error) throw error
      const withUrls = (data || []).map(att => {
        const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(att.file_path)
        return { ...att, url: urlData.publicUrl }
      })
      setAttachmentList(withUrls)
    } catch (err) {
      console.error('Erreur chargement pièces jointes:', err)
      setAttachmentError('Impossible de charger les pièces jointes.')
    } finally {
      setAttachmentLoading(false)
    }
  }, [])

  const loadAttachmentCounts = useCallback(async () => {
    try {
      const { data } = await supabase.from('session_attachments').select('session_id')
      if (!data) return
      const counts = {}
      data.forEach(row => { counts[row.session_id] = (counts[row.session_id] || 0) + 1 })
      setAttachmentCounts(counts)
    } catch (err) {
      console.error('Erreur compteurs pièces jointes:', err)
    }
  }, [])

  const openAttachmentModal = async (session) => {
    setAttachmentSession(session)
    setAttachmentError('')
    setDeleteConfirmIdx(null)
    setShowAttachmentModal(true)
    await loadAttachments(session.id)
  }

  const openMonthSportModal = async (month, sport) => {
    const matchingSessions = allSessions.filter(s => {
      if (!s.date) return false
      const d = new Date(s.date)
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      return key === month.key && getSportName(s) === sport.name
    })
    setMonthSportTitle(`${sport.name} — ${month.label}`)
    setMonthSportSessions(matchingSessions)
    setMonthSportPresences({})
    setShowMonthSportModal(true)
    setMonthSportLoading(true)
    const presencesMap = {}
    await Promise.all(matchingSessions.map(async (session) => {
      const [{ data: members }, { data: coaches }] = await Promise.all([
        supabase.from('presences').select(`id, timestamp, mode, participants:participant_id (first_name, last_name, email, phone)`).eq('session_id', session.id).eq('role', 'member').order('timestamp', { ascending: true }),
        supabase.from('presences').select(`id, timestamp, mode, coaches:coach_id (first_name, last_name, sports)`).eq('session_id', session.id).eq('role', 'coach').order('timestamp', { ascending: true })
      ])
      presencesMap[session.id] = { members: members || [], coaches: coaches || [] }
    }))
    setMonthSportPresences(presencesMap)
    setMonthSportLoading(false)
  }

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || [])
    if (!files.length) return
    setAttachmentError('')
    setAttachmentUploading(true)
    const sessionId = attachmentSession?.id
    const uploaded = []
    for (const file of files) {
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        setAttachmentError(`"${file.name}" : seuls les images et PDF sont acceptés.`)
        continue
      }
      if (file.size > 10 * 1024 * 1024) {
        setAttachmentError(`"${file.name}" dépasse 10 Mo.`)
        continue
      }
      const ext = file.name.split('.').pop()
      const filePath = `${sessionId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
      const { error: uploadError } = await supabase.storage.from(BUCKET).upload(filePath, file, { cacheControl: '3600', upsert: false })
      if (uploadError) { setAttachmentError(`Erreur upload "${file.name}" : ${uploadError.message}`); continue }
      const { data: insertData, error: insertError } = await supabase.from('session_attachments').insert({ session_id: sessionId, file_name: file.name, file_path: filePath, file_type: file.type }).select().single()
      if (insertError) { setAttachmentError(`Erreur enregistrement "${file.name}".`); await supabase.storage.from(BUCKET).remove([filePath]); continue }
      const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(filePath)
      uploaded.push({ ...insertData, url: urlData.publicUrl })
    }
    if (uploaded.length > 0) {
      setAttachmentList(prev => [...prev, ...uploaded])
      setAttachmentCounts(prev => ({ ...prev, [sessionId]: (prev[sessionId] || 0) + uploaded.length }))
    }
    setAttachmentUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const removeAttachment = async (att, idx) => {
    try {
      await supabase.storage.from(BUCKET).remove([att.file_path])
      await supabase.from('session_attachments').delete().eq('id', att.id)
      setAttachmentList(prev => prev.filter((_, i) => i !== idx))
      setAttachmentCounts(prev => ({ ...prev, [attachmentSession?.id]: Math.max(0, (prev[attachmentSession?.id] || 1) - 1) }))
      setDeleteConfirmIdx(null)
    } catch (err) {
      console.error('Erreur suppression:', err)
      setAttachmentError('Erreur lors de la suppression.')
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    if (e.dataTransfer.files.length) handleFileUpload({ target: { files: e.dataTransfer.files } })
  }

  const loadPresenceLists = async (sessionId) => {
    const { data: members } = await supabase.from('presences').select(`id, timestamp, mode, participant_id, participants:participant_id (first_name, last_name, email, phone)`).eq('session_id', sessionId).eq('role', 'member').order('timestamp', { ascending: true })
    setMemberPresences(members || [])
    const { data: coaches } = await supabase.from('presences').select(`id, timestamp, mode, coaches:coach_id (first_name, last_name, sports)`).eq('session_id', sessionId).eq('role', 'coach').order('timestamp', { ascending: true })
    setCoachPresences(coaches || [])
  }

  const openPresenceModal = async (session, startInEdit = false) => {
    setPresenceSession(session)
    setShowPresenceModal(true)
    setPresenceLoading(true)
    setMemberPresences([])
    setCoachPresences([])
    setPresenceEditMode(startInEdit && new Date(session.date) <= new Date())
    setPresenceDirty(false)
    setAddSearch('')
    setAddResults([])
    setPresenceMsg(null)
    try {
      await loadPresenceLists(session.id)
    } catch (err) {
      console.error('Erreur chargement présences:', err)
    } finally {
      setPresenceLoading(false)
    }
  }

  // Fermer la fenêtre ; si la liste a été modifiée, on recharge les compteurs du tableau de bord
  const closePresenceModal = () => {
    setShowPresenceModal(false)
    setPresenceEditMode(false)
    if (presenceDirty) {
      setPresenceDirty(false)
      fetchReportsData()
    }
  }

  const togglePresenceEditMode = () => {
    setPresenceEditMode(prev => !prev)
    setPresenceMsg(null)
    setAddSearch('')
    setAddResults([])
  }

  // Recherche de membres à ajouter (chaque mot doit correspondre au nom ou au prénom)
  const searchParticipantsToAdd = async (term) => {
    setAddSearch(term)
    const words = term.trim().split(/\s+/).filter(w => w.length > 0)
    if (term.trim().length < 2) { setAddResults([]); return }
    setAddSearching(true)
    try {
      let query = supabase.from('participants').select('id, first_name, last_name, birth_date')
      words.forEach(w => {
        const safe = w.replace(/[%,()*]/g, '')
        if (safe) query = query.or(`first_name.ilike.%${safe}%,last_name.ilike.%${safe}%`)
      })
      const { data, error } = await query.order('last_name').limit(10)
      if (error) throw error
      setAddResults(data || [])
    } catch (err) {
      console.error('Erreur recherche membre:', err)
      setAddResults([])
    } finally {
      setAddSearching(false)
    }
  }

  // Ajout manuel : l'heure enregistrée est celle du début de la session (statistiques cohérentes)
  const addManualPresence = async (participant) => {
    if (!presenceSession) return
    setPresenceActionId(participant.id)
    setPresenceMsg(null)
    try {
      const { error } = await supabase.from('presences').upsert(
        [{
          participant_id: participant.id,
          session_id: presenceSession.id,
          mode: 'name',
          role: 'member',
          timestamp: presenceSession.date
        }],
        { onConflict: 'session_id,participant_id', ignoreDuplicates: true }
      )
      if (error) throw error
      await loadPresenceLists(presenceSession.id)
      setPresenceDirty(true)
      setPresenceMsg({ type: 'success', text: `${participant.first_name} ${participant.last_name} a été ajouté(e) à la liste.` })
      setAddSearch('')
      setAddResults([])
    } catch (err) {
      console.error('Erreur ajout présence:', err)
      setPresenceMsg({ type: 'danger', text: 'Erreur lors de l\'ajout : ' + err.message })
    } finally {
      setPresenceActionId(null)
    }
  }

  const removePresence = async (presence) => {
    setPresenceActionId(presence.id)
    setPresenceMsg(null)
    try {
      const { error } = await supabase.from('presences').delete().eq('id', presence.id)
      if (error) throw error
      setMemberPresences(prev => prev.filter(p => p.id !== presence.id))
      setPresenceDirty(true)
      setPresenceMsg({ type: 'success', text: 'Présence retirée de la liste.' })
    } catch (err) {
      console.error('Erreur suppression présence:', err)
      setPresenceMsg({ type: 'danger', text: 'Erreur lors de la suppression : ' + err.message })
    } finally {
      setPresenceActionId(null)
    }
  }

  // ── Création manuelle d'une session : titre, date, heures et lieu saisis librement ──
  const openCreateModal = async (prefillDate = '') => {
    setCreateError('')
    setCreateTitle('')
    setCreateStartTime('')
    setCreateEndTime('')
    setCreateLocation('Street Sport Luxembourg')
    setCreateDate(prefillDate || dateFilter || new Date().toLocaleDateString('en-CA'))
    setShowCreateModal(true)
    // Liste des cours (actifs et archivés) : sert aux suggestions de titre et aux statistiques
    const { data, error } = await supabase
      .from('course_types')
      .select('id, name, day_of_week, start_time, end_time, location, is_active')
      .order('name', { ascending: true })
    if (!error) setCreateCourses(data || [])
  }

  // Si le titre correspond à un cours du programme, on pré-remplit heures et lieu (modifiables)
  const handleCreateTitleChange = (value) => {
    setCreateTitle(value)
    const weekday = createDate ? WEEKDAYS_FR[new Date(`${createDate}T12:00:00`).getDay()] : ''
    const match = findLinkedCourse(createCourses, value, weekday)
    if (match) {
      setCreateStartTime(hhmm(match.start_time))
      setCreateEndTime(hhmm(match.end_time))
      setCreateLocation(match.location || 'Street Sport Luxembourg')
    }
  }

  const createManualSession = async () => {
    const title = createTitle.trim()
    const location = createLocation.trim()
    if (!title || !createDate || !createStartTime || !location) {
      setCreateError('Le titre, la date, l\'heure de début et le lieu sont obligatoires.')
      return
    }
    if (createEndTime && createEndTime <= createStartTime) {
      setCreateError('L\'heure de fin doit être après l\'heure de début.')
      return
    }
    setCreateLoading(true)
    setCreateError('')
    try {
      const weekday = WEEKDAYS_FR[new Date(`${createDate}T12:00:00`).getDay()]
      const linked = findLinkedCourse(createCourses, title, weekday)
      // Même format que les sessions automatiques : date + heure de début
      const dateIso = `${createDate}T${createStartTime}:00+00:00`

      // Anti-doublon : même cours ou même titre le même jour
      const { data: sameDay, error: exErr } = await supabase
        .from('sessions')
        .select('id, title, course_type_id')
        .gte('date', `${createDate}T00:00:00+00:00`)
        .lte('date', `${createDate}T23:59:59+00:00`)
      if (exErr) throw exErr
      const duplicate = (sameDay || []).some(e =>
        (linked && e.course_type_id === linked.id) ||
        (e.title || '').trim().toLowerCase() === title.toLowerCase()
      )
      if (duplicate) {
        setCreateError('Une session « ' + title + ' » existe déjà à cette date. Utilisez le filtre Date pour la retrouver.')
        return
      }

      const { data: inserted, error } = await supabase
        .from('sessions')
        .insert([{
          course_type_id: linked ? linked.id : null,
          title,
          date: dateIso,
          location,
          end_time: createEndTime || null,
          is_manual: true
        }])
        .select()
        .single()
      if (error) throw error

      await fetchReportsData()
      setDateFilter(createDate)
      setShowCreateModal(false)
      // Ouvre directement la liste en mode modification pour ajouter les présents
      openPresenceModal({ ...inserted, course_types: linked || null }, true)
    } catch (err) {
      console.error('Erreur création session:', err)
      const hint = /is_manual|end_time/.test(err.message || '')
        ? ' — Lancez d\'abord le SQL de mise à jour dans Supabase (colonnes is_manual et end_time).'
        : ''
      setCreateError('Erreur lors de la création : ' + err.message + hint)
    } finally {
      setCreateLoading(false)
    }
  }

  const printPresenceList = () => {
    const printContent = `<!DOCTYPE html><html><head><title>Liste de présence</title>
      <style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: Arial, sans-serif; padding: 24px; color: #212529; font-size: 13px; } h1 { color: #1d6fa4; font-size: 18px; margin-bottom: 4px; } .subtitle { color: #666; font-size: 12px; margin-bottom: 20px; } .summary { display: flex; gap: 24px; margin-bottom: 24px; padding: 12px 20px; background: #f0f4ff; border-radius: 8px; border-left: 4px solid #1d6fa4; } .summary-item { text-align: center; } .summary-item .num { font-size: 24px; font-weight: 700; color: #0d47a1; } .summary-item .label { font-size: 11px; color: #555; } .section-title { font-weight: bold; font-size: 13px; margin: 18px 0 8px; padding: 6px 10px; border-radius: 4px; } .section-coaches { background: #fff8e1; color: #856404; border-left: 3px solid #ffc107; } .section-members { background: #e8f4fd; color: #0d47a1; border-left: 3px solid #1d6fa4; } table { width: 100%; border-collapse: collapse; margin-bottom: 16px; } th { background: #1d6fa4; color: white; padding: 7px 10px; text-align: left; font-size: 11px; font-weight: 600; } td { padding: 6px 10px; border-bottom: 1px solid #e9ecef; font-size: 12px; } tr:nth-child(even) td { background: #f8f9fa; } .badge { display: inline-block; padding: 2px 7px; border-radius: 4px; font-size: 10px; font-weight: 600; } .badge-qr { background: #d1fae5; color: #065f46; } .badge-manual { background: #dbeafe; color: #1e40af; } .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #dee2e6; font-size: 11px; color: #999; display: flex; justify-content: space-between; }</style>
      </head><body>
      <h1>📋 Liste de présence — ${presenceSession ? getSessionTitle(presenceSession) : 'Session'}</h1>
      <div class="subtitle">${presenceSession ? new Date(presenceSession.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : ''}${presenceSession ? ` • 📍 ${getSessionLocation(presenceSession)}` : ''}${presenceSession && getSessionTimes(presenceSession) ? ` • ⏰ ${getSessionTimeLabel(presenceSession)}` : ''}</div>
      <div class="summary"><div class="summary-item"><div class="num">${memberPresences.length}</div><div class="label">👥 Membres</div></div><div class="summary-item"><div class="num" style="color:#856404">${coachPresences.length}</div><div class="label">🏅 Coachs</div></div><div class="summary-item"><div class="num" style="color:#16a34a">${memberPresences.length + coachPresences.length}</div><div class="label">Total</div></div></div>
      <div class="section-title section-coaches">🏅 Coachs présents (${coachPresences.length})</div>
      ${coachPresences.length === 0 ? '<p style="color:#999;font-size:12px;margin-bottom:16px;">Aucun coach.</p>' : `<table><thead><tr><th>#</th><th>Nom</th><th>Prénom</th><th>Sports</th><th>Heure</th><th>Mode</th></tr></thead><tbody>${coachPresences.map((p, i) => `<tr><td>${i + 1}</td><td><strong>${p.coaches?.last_name || ''}</strong></td><td>${p.coaches?.first_name || ''}</td><td>${(p.coaches?.sports || []).join(', ') || '—'}</td><td>${formatDateTime(p.timestamp)}</td><td><span class="badge ${p.mode === 'qr' ? 'badge-qr' : 'badge-manual'}">${p.mode === 'qr' ? 'QR Code' : 'Manuel'}</span></td></tr>`).join('')}</tbody></table>`}
      <div class="section-title section-members">👥 Membres présents (${memberPresences.length})</div>
      ${memberPresences.length === 0 ? '<p style="color:#999;font-size:12px;">Aucun membre.</p>' : `<table><thead><tr><th>#</th><th>Nom</th><th>Prénom</th><th>Email</th><th>Téléphone</th><th>Heure</th><th>Mode</th></tr></thead><tbody>${memberPresences.map((p, i) => `<tr><td>${i + 1}</td><td><strong>${p.participants?.last_name || ''}</strong></td><td>${p.participants?.first_name || ''}</td><td>${p.participants?.email || '—'}</td><td>${p.participants?.phone || '—'}</td><td>${formatDateTime(p.timestamp)}</td><td><span class="badge ${p.mode === 'qr' ? 'badge-qr' : 'badge-manual'}">${p.mode === 'qr' ? 'QR Code' : 'Manuel'}</span></td></tr>`).join('')}</tbody></table>`}
      <div class="footer"><span>Street Sport Luxembourg</span><span>Imprimé le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span></div>
      </body></html>`
    const win = window.open('', '_blank')
    win.document.write(printContent)
    win.document.close()
    win.focus()
    setTimeout(() => { win.print(); win.close() }, 300)
  }

  const calculateMonthlyStats = (sessions) => {
    const monthMap = {}
    sessions.forEach(session => {
      if (!session.date) return
      const date = new Date(session.date)
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const label = date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
      if (!monthMap[key]) monthMap[key] = { key, label, sessions: 0, presences: 0, sports: {} }
      monthMap[key].sessions++
      const presencesCount = session.presences?.length || 0
      monthMap[key].presences += presencesCount
      const sportName = getSportName(session)
      if (!monthMap[key].sports[sportName]) monthMap[key].sports[sportName] = { sessions: 0, presences: 0 }
      monthMap[key].sports[sportName].sessions++
      monthMap[key].sports[sportName].presences += presencesCount
    })
    setMonthlyStats(
      Object.values(monthMap).sort((a, b) => b.key.localeCompare(a.key)).slice(0, 6)
        .map(m => ({ ...m, sportsArray: Object.entries(m.sports || {}).map(([name, data]) => ({ name, ...data })).sort((a, b) => b.presences - a.presences) }))
    )
  }

  // ✅ CORRECTION : utilise COUNT() en base — plus de limite à 1000 lignes
  const fetchReportsData = useCallback(async () => {
    try {
      const [
        { data: sessionsData },
        { count: totalSessionsCount },
        { count: participantsCount },
        { count: presencesCount },
        { data: allSessionsData }
      ] = await Promise.all([
        supabase.from('sessions').select('*').gte('date', new Date().toISOString()),
        supabase.from('sessions').select('*', { count: 'exact', head: true }),
        supabase.from('participants').select('*', { count: 'exact', head: true }),
        supabase.from('presences').select('*', { count: 'exact', head: true }),
        supabase.from('sessions')
          .select(`*, course_types (name, description, location, day_of_week, start_time, end_time), presences (id)`)
          .order('date', { ascending: false })
      ])

      // ✅ Nombre de participants distincts ayant au moins une présence
      const { data: distinctData } = await supabase.rpc('get_distinct_participants_count')

      const sessionsCount = sessionsData?.length || 0
      const totalParticipants = participantsCount || 0
      const totalPresences = presencesCount || 0
      const distinctParticipants = distinctData || 0

      // ✅ Taux = participants ayant assisté à au moins 1 session / total inscrits
      const participationRate = totalParticipants > 0
        ? Math.round((distinctParticipants / totalParticipants) * 100)
        : 0

      setStats({
        sessionsCount,
        participantsCount: totalParticipants,
        presencesCount: totalPresences,
        participationRate
      })

      setAllSessions(allSessionsData || [])
      calculateMonthlyStats(allSessionsData || [])
      await loadAttachmentCounts()
    } catch (error) {
      console.error('Error fetching reports data:', error)
    } finally {
      setLoading(false)
    }
  }, [loadAttachmentCounts])

  const filterSessions = useCallback(() => {
    let filtered = allSessions
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filtered = filtered.filter(s => getSessionTitle(s).toLowerCase().includes(term) || getSessionLocation(s).toLowerCase().includes(term))
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(s => { const isFuture = new Date(s.date) > new Date(); return statusFilter === 'future' ? isFuture : !isFuture })
    }
    if (dateFilter) {
      // Compare le jour local de la session (format AAAA-MM-JJ) avec la date choisie
      filtered = filtered.filter(s => s.date && new Date(s.date).toLocaleDateString('en-CA') === dateFilter)
    }
    setFilteredSessions(filtered)
    setCurrentPage(1)
  }, [allSessions, searchTerm, statusFilter, dateFilter])

  useEffect(() => {
    const getUser = async () => { const { data: { user } } = await supabase.auth.getUser(); setUser(user) }
    getUser()
    fetchReportsData()
  }, [fetchReportsData])

  useEffect(() => { filterSessions() }, [filterSessions])

  const indexOfLastSession = currentPage * sessionsPerPage
  const indexOfFirstSession = indexOfLastSession - sessionsPerPage
  const currentSessions = filteredSessions.slice(indexOfFirstSession, indexOfLastSession)
  const totalPages = Math.ceil(filteredSessions.length / sessionsPerPage)

  // Données de la fenêtre « Créer une session »
  const createWeekday = createDate ? WEEKDAYS_FR[new Date(`${createDate}T12:00:00`).getDay()] : ''
  const createTitleOptions = [...new Set(createCourses.map(c => (c.name || '').trim()).filter(Boolean))].sort((x, y) => x.localeCompare(y, 'fr'))
  const createLinkedCourse = createTitle.trim() ? findLinkedCourse(createCourses, createTitle, createWeekday) : null

  const formatDate = (d) => new Date(d).toLocaleDateString('fr-FR')
  const formatDateTime = (ts) => new Date(ts).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  const getDayName = (d) => new Date(d).toLocaleDateString('fr-FR', { weekday: 'long' })

  const downloadSessionData = async (sessionId, sessionTitle) => {
    try {
      const { data: members } = await supabase.from('presences').select(`timestamp, mode, participants:participant_id (first_name, last_name, email, phone)`).eq('session_id', sessionId).eq('role', 'member')
      const { data: coaches } = await supabase.from('presences').select(`timestamp, mode, coaches:coach_id (first_name, last_name, sports)`).eq('session_id', sessionId).eq('role', 'coach')
      const rows = [['Rôle', 'Nom', 'Prénom', 'Email', 'Téléphone / Sports', 'Heure', 'Mode'], ...(members || []).map(p => ['Membre', p.participants?.last_name || '', p.participants?.first_name || '', p.participants?.email || '', p.participants?.phone || '', p.timestamp ? new Date(p.timestamp).toLocaleString('fr-FR') : '', p.mode === 'qr' ? 'QR Code' : 'Manuel']), ...(coaches || []).map(p => ['Coach', p.coaches?.last_name || '', p.coaches?.first_name || '', '', (p.coaches?.sports || []).join(' | '), p.timestamp ? new Date(p.timestamp).toLocaleString('fr-FR') : '', p.mode === 'qr' ? 'QR Code' : 'Manuel'])]
      const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `presences_${(sessionTitle || 'Session').replace(/\s+/g, '_')}_${formatDate(new Date())}.csv`)
      document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url)
    } catch (error) { alert('Erreur lors du téléchargement des données') }
  }

  const downloadAllSessions = () => {
    const csvContent = [['Jour', 'Date', 'Heure', 'Lieu', 'Titre', 'Nombre de présences', 'Statut'], ...filteredSessions.map(s => [getDayName(s.date), formatDate(s.date), getSessionTimeLabel(s), getSessionLocation(s), getSessionTitle(s), s.presences?.length || 0, new Date(s.date) > new Date() ? 'À venir' : 'Terminée'])].map(r => r.join(',')).join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `rapport_sessions_complet_${formatDate(new Date())}.csv`)
    document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url)
  }

  const printMonthStats = (month) => {
    const colorMap = ['#3b82f6', '#22c55e', '#f59e0b', '#06b6d4', '#ef4444', '#6b7280']
    const maxPresences = (month.sportsArray || [])[0]?.presences || 1
    const sportsRows = (month.sportsArray || []).map((sport, si) => { const color = colorMap[si % colorMap.length]; const pct = Math.round((sport.presences / maxPresences) * 100); return `<tr><td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;font-weight:600;color:#374151">${sport.name}</td><td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:center"><span style="background:${color};color:#fff;padding:2px 10px;border-radius:4px;font-size:12px;font-weight:600">${sport.presences} présence${sport.presences > 1 ? 's' : ''}</span></td><td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;text-align:center;color:#6b7280;font-size:13px">${sport.sessions} séance${sport.sessions > 1 ? 's' : ''}</td><td style="padding:8px 10px;border-bottom:1px solid #f3f4f6;width:35%"><div style="background:#f3f4f6;border-radius:4px;height:10px;overflow:hidden"><div style="background:${color};height:100%;width:${pct}%;border-radius:4px"></div></div></td></tr>` }).join('')
    const printContent = `<!DOCTYPE html><html><head><title>Rapport mensuel — ${month.label}</title><style>* { box-sizing: border-box; margin: 0; padding: 0; } body { font-family: Arial, sans-serif; padding: 28px; color: #212529; font-size: 13px; } h1 { color: #d97706; font-size: 20px; margin-bottom: 4px; text-transform: capitalize; } .subtitle { color: #6b7280; font-size: 12px; margin-bottom: 24px; } .summary { display: flex; gap: 20px; margin-bottom: 28px; } .summary-card { flex: 1; text-align: center; padding: 14px; border-radius: 8px; } table { width: 100%; border-collapse: collapse; } thead th { background: #f59e0b; color: white; padding: 9px 10px; text-align: left; font-size: 12px; } .footer { margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #9ca3af; display: flex; justify-content: space-between; }</style></head><body><h1>📆 Rapport mensuel — ${month.label}</h1><div class="subtitle">Street Sport Luxembourg • Généré le ${new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</div><div class="summary"><div class="summary-card" style="background:#fffbeb;border:1px solid #fde68a"><div style="font-size:28px;font-weight:700;color:#d97706">${month.sessions}</div><div style="font-size:11px;color:#6b7280">Sessions</div></div><div class="summary-card" style="background:#eff6ff;border:1px solid #bfdbfe"><div style="font-size:28px;font-weight:700;color:#2563eb">${month.presences}</div><div style="font-size:11px;color:#6b7280">Présences</div></div><div class="summary-card" style="background:#f0fdf4;border:1px solid #bbf7d0"><div style="font-size:28px;font-weight:700;color:#16a34a">${month.sportsArray?.length || 0}</div><div style="font-size:11px;color:#6b7280">Sports</div></div></div><table><thead><tr><th>Sport / Cours</th><th style="text-align:center">Présences</th><th style="text-align:center">Séances</th><th>Activité</th></tr></thead><tbody>${sportsRows}</tbody></table><div class="footer"><span>Street Sport Luxembourg</span><span>Imprimé le ${new Date().toLocaleString('fr-FR')}</span></div></body></html>`
    const win = window.open('', '_blank')
    win.document.write(printContent)
    win.document.close()
    win.focus()
    setTimeout(() => { win.print(); win.close() }, 300)
  }

  if (loading) return (
    <ProtectedRoute><Layout user={user}>
      <Container className="text-center py-5">
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Chargement...</span></div>
        <p className="mt-3">Chargement des rapports...</p>
      </Container>
    </Layout></ProtectedRoute>
  )

  return (
    <ProtectedRoute>
      <Layout user={user}>
        <Container fluid="lg">

          {/* En-tête */}
          <div className="text-center mb-5">
            <div className="mb-3" style={{ fontSize: '4rem' }}>🥊</div>
            <h1 className="display-5 fw-bold text-primary">Street Sport Luxembourg</h1>
            <p className="lead text-muted">Tableau de bord et rapports des activités</p>
          </div>

          {/* Cartes stats */}
          <Row className="g-4 mb-5">
            <Col md={3} sm={6}><Card className="h-100 shadow-sm border-0"><Card.Body className="text-center"><div className="text-primary mb-2" style={{ fontSize: '3rem' }}>📅</div><Card.Title className="h2 fw-bold text-primary">{stats.sessionsCount}</Card.Title><Card.Text className="text-muted">Sessions actives</Card.Text></Card.Body></Card></Col>
            <Col md={3} sm={6}><Card className="h-100 shadow-sm border-0"><Card.Body className="text-center"><div className="text-success mb-2" style={{ fontSize: '3rem' }}>👥</div><Card.Title className="h2 fw-bold text-success">{stats.participantsCount}</Card.Title><Card.Text className="text-muted">Participants inscrits</Card.Text></Card.Body></Card></Col>
            <Col md={3} sm={6}><Card className="h-100 shadow-sm border-0"><Card.Body className="text-center"><div className="text-warning mb-2" style={{ fontSize: '3rem' }}>👤</div><Card.Title className="h2 fw-bold text-warning">{stats.presencesCount}</Card.Title><Card.Text className="text-muted">Présences totales</Card.Text></Card.Body></Card></Col>
            <Col md={3} sm={6}><Card className="h-100 shadow-sm border-0"><Card.Body className="text-center"><div className="text-info mb-2" style={{ fontSize: '3rem' }}>📈</div><Card.Title className="h2 fw-bold text-info">{stats.participationRate}%</Card.Title><Card.Text className="text-muted">Taux de participation</Card.Text></Card.Body></Card></Col>
          </Row>

          {/* Stats mensuelles */}
          <Row className="g-4 mb-5">
            <Col md={12}>
              <Card className="shadow-sm border-0">
                <Card.Header style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff' }}>
                  <h5 className="mb-0">📆 Nombre de cours par mois</h5>
                </Card.Header>
                <Card.Body>
                  {monthlyStats.length === 0 ? <div className="text-center text-muted py-3">Aucune donnée disponible</div> : (
                    <Row className="g-3">
                      {monthlyStats.map((month, i) => (
                        <Col md={4} sm={6} xs={12} key={month.key}>
                          <div className="rounded h-100" style={{ border: i === 0 ? '2px solid #f59e0b' : '1px solid #fde68a', background: i === 0 ? 'linear-gradient(135deg, #fffbeb, #fef3c7)' : '#fafafa', boxShadow: i === 0 ? '0 3px 12px rgba(245,158,11,0.2)' : 'none', overflow: 'hidden' }}>
                            <div className="d-flex align-items-center justify-content-between px-3 py-2" style={{ background: i === 0 ? '#fef3c7' : '#f3f4f6', borderBottom: '1px solid #fde68a' }}>
                              <div>
                                <span className="fw-bold text-capitalize" style={{ fontSize: '0.95rem', color: '#92400e' }}>{month.label}</span>
                                {i === 0 && <Badge bg="warning" text="dark" className="ms-2" style={{ fontSize: '0.62rem' }}>Mois actuel</Badge>}
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <div className="text-end">
                                  <span style={{ fontSize: '1.4rem', fontWeight: 700, color: '#d97706' }}>{month.sessions}</span>
                                  <div style={{ fontSize: '0.68rem', color: '#92400e', lineHeight: 1 }}>session{month.sessions > 1 ? 's' : ''}</div>
                                </div>
                                <Button variant="outline-warning" size="sm" onClick={() => printMonthStats(month)} style={{ fontSize: '0.7rem', padding: '2px 7px', whiteSpace: 'nowrap' }}>🖨️</Button>
                              </div>
                            </div>
                            <div className="d-flex align-items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid #fde68a' }}>
                              <Badge bg="primary" style={{ fontSize: '0.72rem' }}>{month.presences} présence{month.presences > 1 ? 's' : ''} au total</Badge>
                            </div>
                            <div className="px-3 py-2">
                              {(month.sportsArray || []).map((sport, si) => {
                                const colors = ['primary', 'success', 'warning', 'info', 'danger', 'secondary']
                                const color = colors[si % colors.length]
                                const maxPresences = (month.sportsArray || [])[0]?.presences || 1
                                const pct = Math.round((sport.presences / maxPresences) * 100)
                                return (
                                  <div key={sport.name} className="mb-2" onClick={() => openMonthSportModal(month, sport)} style={{ cursor: 'pointer', borderRadius: 6, padding: '4px 6px', transition: 'background 0.15s' }} onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.05)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'} title={`Voir les ${sport.sessions} session(s) de ${sport.name}`}>
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                      <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'underline dotted', textUnderlineOffset: 3 }} title={sport.name}>{sport.name}</span>
                                      <div className="d-flex align-items-center gap-1">
                                        <Badge bg={color} style={{ fontSize: '0.65rem' }}>{sport.presences} prés.</Badge>
                                        <span style={{ fontSize: '0.65rem', color: '#9ca3af' }}>{sport.sessions} séance{sport.sessions > 1 ? 's' : ''}</span>
                                        <span style={{ fontSize: '0.65rem', color: '#d97706', fontWeight: 700 }}>→</span>
                                      </div>
                                    </div>
                                    <div className="progress" style={{ height: '6px', borderRadius: '3px' }}>
                                      <div className={`progress-bar bg-${color}`} style={{ width: `${pct}%`, borderRadius: '3px' }} />
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Filtres */}
          <Card className="shadow-sm border-0 mb-4">
            <Card.Body>
              <Row className="g-3 align-items-center">
                <Col lg={3} md={6}>
                  <InputGroup>
                    <InputGroup.Text>🔍</InputGroup.Text>
                    <Form.Control type="text" placeholder="Rechercher par titre ou lieu..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </InputGroup>
                </Col>
                <Col lg={2} md={6}>
                  <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">Tous les statuts</option>
                    <option value="future">À venir</option>
                    <option value="past">Terminées</option>
                  </Form.Select>
                </Col>
                <Col lg={3} md={6}>
                  <InputGroup>
                    <InputGroup.Text>📅 Date</InputGroup.Text>
                    <Form.Control type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} />
                  </InputGroup>
                </Col>
                <Col lg={4} md={6}>
                  <div className="d-flex gap-2">
                    {(searchTerm || statusFilter !== 'all' || dateFilter) && (
                      <Button variant="outline-secondary" title="Réinitialiser les filtres" onClick={() => { setSearchTerm(''); setStatusFilter('all'); setDateFilter('') }}>✕</Button>
                    )}
                    <Button variant="success" className="flex-grow-1" onClick={downloadAllSessions}>
                      {(searchTerm || statusFilter !== 'all' || dateFilter) ? `📥 Télécharger la sélection (${filteredSessions.length})` : '📥 Télécharger tout'}
                    </Button>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Tableau sessions */}
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
              <h4 className="mb-0">📋 Sessions Récentes <Badge bg="light" text="dark" className="ms-2">{filteredSessions.length}</Badge></h4>
              <div className="d-flex align-items-center gap-3">
                <Button variant="light" size="sm" onClick={() => openCreateModal()}>➕ Créer une session</Button>
                <small>Page {currentPage} sur {totalPages}</small>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {currentSessions.length > 0 ? (
                <>
                  <Table responsive hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>📅 Jour</th><th>📆 Date</th><th>⏰ Heure</th><th>📍 Lieu</th><th>🏷️ Titre</th>
                        <th className="text-center">👤 Présences</th>
                        <th className="text-center">📊 Statut</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentSessions.map((session) => {
                        const count = attachmentCounts[session.id] || 0
                        return (
                          <tr key={session.id}>
                            <td className="fw-medium text-capitalize">{getDayName(session.date)}</td>
                            <td>{formatDate(session.date)}</td>
                            <td>{getSessionTimeLabel(session)}</td>
                            <td>{getSessionLocation(session)}</td>
                            <td className="fw-medium">{getSessionTitle(session)}</td>
                            <td className="text-center"><Badge bg="primary" className="fs-6">{session.presences?.length || 0}</Badge></td>
                            <td className="text-center">
                              <Badge bg={new Date(session.date) > new Date() ? 'success' : 'secondary'}>
                                {new Date(session.date) > new Date() ? 'À venir' : 'Terminée'}
                              </Badge>
                            </td>
                            <td className="text-center">
                              <div className="d-flex gap-1 justify-content-center flex-wrap">
                                <Button variant="outline-primary" size="sm" onClick={() => openPresenceModal(session)}>👁 Liste</Button>
                                <Button variant="outline-success" size="sm" onClick={() => downloadSessionData(session.id, getSessionTitle(session))}>📥 CSV</Button>
                                {new Date(session.date) <= new Date() && (
                                  <Button variant="outline-warning" size="sm" onClick={() => openPresenceModal(session, true)} title="Ajouter ou retirer des présences">✏️ Modifier</Button>
                                )}
                                <Button variant={count > 0 ? 'warning' : 'outline-primary'} size="sm" onClick={() => openAttachmentModal(session)} title={count > 0 ? `${count} pièce(s) jointe(s)` : 'Ajouter une liste de présence'} style={{ position: 'relative', minWidth: 80 }}>
                                  📎 {count > 0 ? `Voir les documents (${count})` : 'Ajouter une liste de présence'}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                  {totalPages > 1 && (
                    <div className="d-flex justify-content-center py-3 border-top">
                      <Pagination>
                        <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>◀</Pagination.Prev>
                        {[...Array(totalPages)].map((_, i) => <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>{i + 1}</Pagination.Item>)}
                        <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>▶</Pagination.Next>
                      </Pagination>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <div style={{ fontSize: '4rem' }}>📭</div>
                  <p className="mt-3 text-muted">Aucune session trouvée</p>
                  <Button variant="success" className="mb-2" onClick={() => openCreateModal(dateFilter)}>➕ Créer une session{dateFilter ? ' pour cette date' : ''}</Button>
                  {(searchTerm || statusFilter !== 'all' || dateFilter) && <Button variant="outline-primary" onClick={() => { setSearchTerm(''); setStatusFilter('all'); setDateFilter('') }}>🔄 Réinitialiser les filtres</Button>}
                </div>
              )}
            </Card.Body>
          </Card>

        </Container>
      </Layout>

      {/* ════════════ Modal création manuelle d'une session ════════════ */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #15803d, #16a34a)', color: '#fff' }}>
          <Modal.Title className="fw-bold fs-5">➕ Créer une session manuellement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {createError && <Alert variant="danger" className="py-2 small">{createError}</Alert>}

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Titre de la session *</Form.Label>
            <Form.Control
              type="text"
              list="session-title-options"
              value={createTitle}
              onChange={(e) => handleCreateTitleChange(e.target.value)}
              placeholder="Choisir dans la liste ou écrire un titre"
              autoComplete="off"
            />
            <datalist id="session-title-options">
              {createTitleOptions.map(n => <option key={n} value={n} />)}
            </datalist>
            {createLinkedCourse && (
              <Form.Text className="text-muted">
                Rattachée au cours « {createLinkedCourse.name} » : elle compte dans les statistiques de ce sport.
              </Form.Text>
            )}
          </Form.Group>

          <Row className="g-3 mb-3">
            <Col md={4}>
              <Form.Label className="fw-semibold">Date *</Form.Label>
              <Form.Control type="date" value={createDate} onChange={(e) => setCreateDate(e.target.value)} />
              {createWeekday && <Form.Text className="text-muted">{createWeekday}</Form.Text>}
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold">Début *</Form.Label>
              <Form.Control type="time" value={createStartTime} onChange={(e) => setCreateStartTime(e.target.value)} />
            </Col>
            <Col md={4}>
              <Form.Label className="fw-semibold">Fin</Form.Label>
              <Form.Control type="time" value={createEndTime} onChange={(e) => setCreateEndTime(e.target.value)} />
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label className="fw-semibold">Lieu *</Form.Label>
            <Form.Control type="text" value={createLocation} onChange={(e) => setCreateLocation(e.target.value)} placeholder="Ex : Street Sport Luxembourg" />
          </Form.Group>

          {createLinkedCourse && createLinkedCourse.is_active === false && (
            <Alert variant="info" className="py-2 small mb-0">
              📦 Ce titre correspond à un cours <strong>archivé</strong>. La session sera créée uniquement pour cette date ; le cours reste archivé.
            </Alert>
          )}
          <div className="text-muted small mt-3">
            Si le titre existe dans le programme, l'heure et le lieu sont pré-remplis ; vous pouvez les modifier. Après la création, la liste de présence s'ouvre pour ajouter les membres présents.
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={createLoading}>Annuler</Button>
          <Button variant="success" onClick={createManualSession} disabled={createLoading}>
            {createLoading ? <><Spinner size="sm" animation="border" className="me-1" />Création...</> : '✅ Créer et ouvrir la liste'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ════════════ Modal liste de présence ════════════ */}
      <Modal show={showPresenceModal} onHide={closePresenceModal} size="xl" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #1d6fa4, #0d47a1)', color: '#fff' }}>
          <div>
            <Modal.Title className="fw-bold fs-5">📋 Liste de présence — {presenceSession ? getSessionTitle(presenceSession) : ''}</Modal.Title>
            <div style={{ fontSize: '0.82rem', opacity: 0.85, marginTop: 3 }}>
              {presenceSession && new Date(presenceSession.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {presenceSession && ` • 📍 ${getSessionLocation(presenceSession)}`}
              {presenceSession && getSessionTimes(presenceSession) && ` • ⏰ ${getSessionTimeLabel(presenceSession)}`}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-0">
          {presenceLoading ? <div className="text-center py-5"><Spinner animation="border" variant="primary" /><p className="mt-3 text-muted">Chargement...</p></div> : (
            <>
              <div className="d-flex align-items-center gap-4 px-4 py-3 bg-light border-bottom flex-wrap">
                <div className="text-center"><div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#0d47a1' }}>{memberPresences.length}</div><small className="text-muted fw-semibold">👥 Membres</small></div>
                <div style={{ width: 1, height: 40, background: '#dee2e6' }} />
                <div className="text-center"><div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#856404' }}>{coachPresences.length}</div><small className="text-muted fw-semibold">🏅 Coachs</small></div>
                <div style={{ width: 1, height: 40, background: '#dee2e6' }} />
                <div className="text-center"><div style={{ fontSize: '1.6rem', fontWeight: 700, color: '#16a34a' }}>{memberPresences.length + coachPresences.length}</div><small className="text-muted fw-semibold">Total</small></div>
                <div className="ms-auto d-flex gap-2 flex-wrap">
                  {presenceSession && new Date(presenceSession.date) <= new Date() && (
                    <Button variant={presenceEditMode ? 'primary' : 'outline-primary'} size="sm" onClick={togglePresenceEditMode}>
                      {presenceEditMode ? '✅ Terminer la modification' : '✏️ Modifier la liste'}
                    </Button>
                  )}
                  <Button variant="outline-secondary" size="sm" onClick={printPresenceList}>🖨️ Imprimer</Button>
                  <Button variant="success" size="sm" onClick={() => downloadSessionData(presenceSession?.id, presenceSession ? getSessionTitle(presenceSession) : 'Session')}>📥 Exporter CSV</Button>
                </div>
              </div>
              {presenceEditMode && (
                <div className="px-4 py-3 border-bottom" style={{ background: '#f0f9ff' }}>
                  <div className="fw-bold mb-2" style={{ color: '#0d47a1', fontSize: '0.9rem' }}>➕ Ajouter un membre à la liste</div>
                  <InputGroup>
                    <InputGroup.Text>🔍</InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Tapez au moins 2 lettres du nom ou du prénom..."
                      value={addSearch}
                      onChange={(e) => searchParticipantsToAdd(e.target.value)}
                      autoFocus
                    />
                    {addSearching && <InputGroup.Text><Spinner size="sm" animation="border" /></InputGroup.Text>}
                  </InputGroup>
                  {presenceMsg && (
                    <Alert variant={presenceMsg.type} className="py-2 small mt-2 mb-0" dismissible onClose={() => setPresenceMsg(null)}>{presenceMsg.text}</Alert>
                  )}
                  {addResults.length > 0 && (
                    <div className="mt-2 bg-white border rounded" style={{ maxHeight: 260, overflowY: 'auto' }}>
                      {addResults.map(r => {
                        const already = memberPresences.some(p => p.participant_id === r.id)
                        return (
                          <div key={r.id} className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
                            <div>
                              <strong>{r.last_name}</strong> {r.first_name}
                              {r.birth_date && <small className="text-muted ms-2">né(e) le {new Date(r.birth_date).toLocaleDateString('fr-FR')}</small>}
                            </div>
                            {already ? (
                              <Badge bg="success">✅ Déjà présent</Badge>
                            ) : (
                              <Button variant="primary" size="sm" disabled={presenceActionId === r.id} onClick={() => addManualPresence(r)}>
                                {presenceActionId === r.id ? <Spinner size="sm" animation="border" /> : '+ Ajouter'}
                              </Button>
                            )}
                          </div>
                        )
                      })}
                    </div>
                  )}
                  {addSearch.trim().length >= 2 && !addSearching && addResults.length === 0 && (
                    <div className="text-muted small mt-2">Aucun membre trouvé pour « {addSearch} ».</div>
                  )}
                </div>
              )}
              <div className="px-4 py-3 border-bottom">
                <h6 className="fw-bold mb-3 d-flex align-items-center gap-2" style={{ color: '#856404' }}><span>🏅 Coachs présents</span><Badge bg="warning" text="dark">{coachPresences.length}</Badge></h6>
                {coachPresences.length === 0 ? <Alert variant="warning" className="py-2 mb-0 small">Aucun coach n'a signé pour cette session.</Alert> : (
                  <Table responsive hover size="sm" className="mb-0 align-middle">
                    <thead><tr className="bg-light"><th style={{ width: 32 }}>#</th><th>Nom</th><th>Prénom</th><th>Sports enseignés</th><th className="text-center">Heure</th><th className="text-center">Mode</th><th className="text-center">Statut</th></tr></thead>
                    <tbody>{coachPresences.map((p, i) => <tr key={p.id}><td className="text-muted small">{i + 1}</td><td className="fw-bold">{p.coaches?.last_name}</td><td>{p.coaches?.first_name}</td><td><div className="d-flex flex-wrap gap-1">{(p.coaches?.sports || []).map(s => <Badge key={s} bg="primary" style={{ fontSize: '0.68rem' }}>{s}</Badge>)}</div></td><td className="text-center"><Badge bg="warning" text="dark">🕒 {formatDateTime(p.timestamp)}</Badge></td><td className="text-center"><Badge bg={p.mode === 'qr' ? 'success' : 'info'}>{p.mode === 'qr' ? '📱 QR' : '👤 Manuel'}</Badge></td><td className="text-center"><span style={{ display: 'inline-block', border: '1px solid #ced4da', borderRadius: 6, padding: '2px 10px', fontSize: '0.72rem', color: '#198754', background: '#f0fff4', fontWeight: 600 }}>✅ Signé {formatDateTime(p.timestamp)}</span></td></tr>)}</tbody>
                  </Table>
                )}
              </div>
              <div className="px-4 py-3">
                <h6 className="fw-bold mb-3 d-flex align-items-center gap-2 text-primary"><span>👥 Membres présents</span><Badge bg="primary">{memberPresences.length}</Badge></h6>
                {memberPresences.length === 0 ? <Alert variant="info" className="py-2 mb-0 small">Aucun membre présent pour cette session.</Alert> : (
                  <Table responsive hover size="sm" className="mb-0 align-middle">
                    <thead><tr className="bg-light"><th style={{ width: 32 }}>#</th><th>Nom</th><th>Prénom</th><th>Email</th><th>Téléphone</th><th className="text-center">Heure d'arrivée</th><th className="text-center">Mode</th>{presenceEditMode && <th className="text-center" style={{ width: 60 }}></th>}</tr></thead>
                    <tbody>{memberPresences.map((p, i) => <tr key={p.id}><td className="text-muted small">{i + 1}</td><td className="fw-bold">{p.participants?.last_name}</td><td>{p.participants?.first_name}</td><td><small className="text-muted">{p.participants?.email || '—'}</small></td><td><small className="text-muted">{p.participants?.phone || '—'}</small></td><td className="text-center"><Badge bg="success">🕒 {formatDateTime(p.timestamp)}</Badge></td><td className="text-center"><Badge bg={p.mode === 'qr' ? 'success' : 'info'}>{p.mode === 'qr' ? '📱 QR' : '👤 Manuel'}</Badge></td>{presenceEditMode && <td className="text-center"><Button variant="outline-danger" size="sm" disabled={presenceActionId === p.id} title="Retirer de la liste" onClick={() => { if (window.confirm(`Retirer ${p.participants?.first_name || ''} ${p.participants?.last_name || ''} de la liste de présence ?`)) removePresence(p) }}>🗑</Button></td>}</tr>)}</tbody>
                  </Table>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={printPresenceList}>🖨️ Imprimer</Button>
          <Button variant="secondary" onClick={closePresenceModal}>Fermer</Button>
        </Modal.Footer>
      </Modal>

      {/* ════════════ Modal pièces jointes ════════════ */}
      <Modal show={showAttachmentModal} onHide={() => setShowAttachmentModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #374151, #1f2937)', color: '#fff' }}>
          <div>
            <Modal.Title className="fw-bold fs-5">📎 Pièces jointes — {attachmentSession ? getSessionTitle(attachmentSession) : 'Session'}</Modal.Title>
            <div style={{ fontSize: '0.82rem', opacity: 0.85, marginTop: 3 }}>
              {attachmentSession && new Date(attachmentSession.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              {attachmentSession && ` • 📍 ${getSessionLocation(attachmentSession)}`}
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div onDragOver={(e) => e.preventDefault()} onDrop={handleDrop} onClick={() => fileInputRef.current?.click()} style={{ border: '2px dashed #d1d5db', borderRadius: 10, background: '#f9fafb', padding: '28px 20px', textAlign: 'center', cursor: 'pointer', marginBottom: 20 }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>📁</div>
            <p className="mb-1 fw-semibold text-secondary">Cliquez ou glissez-déposez un fichier ici</p>
            <p className="mb-0 text-muted" style={{ fontSize: '0.8rem' }}>Images (JPG, PNG…) ou PDF • Max 10 Mo par fichier</p>
            <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple style={{ display: 'none' }} onChange={handleFileUpload} />
          </div>
          {attachmentError && <Alert variant="danger" className="py-2 small mb-3" dismissible onClose={() => setAttachmentError('')}>{attachmentError}</Alert>}
          {attachmentUploading && (<div className="text-center py-2 mb-3"><Spinner size="sm" animation="border" variant="primary" className="me-2" /><span className="text-muted small">Upload en cours vers Supabase…</span></div>)}
          {attachmentLoading ? (
            <div className="text-center py-4"><Spinner animation="border" variant="secondary" size="sm" /><span className="ms-2 text-muted small">Chargement des fichiers…</span></div>
          ) : attachmentList.length === 0 ? (
            <div className="text-center text-muted py-3"><div style={{ fontSize: '2.5rem' }}>🗂️</div><p className="mt-2 small">Aucune pièce jointe pour cette session.<br />Ajoutez une photo ou un scan de la liste papier.</p></div>
          ) : (
            <div>
              <h6 className="fw-bold mb-3 text-secondary">Fichiers enregistrés ({attachmentList.length})</h6>
              <Row className="g-3">
                {attachmentList.map((att, idx) => (
                  <Col xs={12} sm={6} key={att.id}>
                    <div className="border rounded p-2 d-flex align-items-center gap-2 bg-white" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                      {att.file_type?.startsWith('image/') ? (
                        <img src={att.url} alt={att.file_name} onClick={() => window.open(att.url, '_blank')} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, border: '1px solid #e5e7eb', flexShrink: 0, cursor: 'pointer' }} />
                      ) : (
                        <div onClick={() => window.open(att.url, '_blank')} style={{ width: 60, height: 60, background: '#fee2e2', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2rem', flexShrink: 0, cursor: 'pointer' }}>📄</div>
                      )}
                      <div className="flex-grow-1 overflow-hidden" style={{ cursor: 'pointer' }} onClick={() => window.open(att.url, '_blank')}>
                        <div className="fw-semibold text-truncate" style={{ fontSize: '0.82rem' }} title={att.file_name}>{att.file_name}</div>
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>{att.file_type?.startsWith('image/') ? '🖼️ Image' : '📄 PDF'}{att.created_at && ` • ${new Date(att.created_at).toLocaleDateString('fr-FR')} à ${new Date(att.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`}</div>
                      </div>
                      <div className="d-flex flex-column gap-1">
                        <Button variant="outline-primary" size="sm" style={{ fontSize: '0.65rem', padding: '2px 7px' }} onClick={() => window.open(att.url, '_blank')} title="Ouvrir">👁</Button>
                        {deleteConfirmIdx === idx ? (
                          <div className="d-flex gap-1">
                            <Button variant="danger" size="sm" style={{ fontSize: '0.6rem', padding: '2px 5px' }} onClick={() => removeAttachment(att, idx)} title="Confirmer suppression">✓</Button>
                            <Button variant="outline-secondary" size="sm" style={{ fontSize: '0.6rem', padding: '2px 5px' }} onClick={() => setDeleteConfirmIdx(null)} title="Annuler">✕</Button>
                          </div>
                        ) : (
                          <Button variant="outline-danger" size="sm" style={{ fontSize: '0.65rem', padding: '2px 7px' }} onClick={() => setDeleteConfirmIdx(idx)} title="Supprimer">🗑</Button>
                        )}
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <small className="text-muted">💾 Fichiers sauvegardés dans Supabase Storage.</small>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={attachmentUploading}>
              {attachmentUploading ? <><Spinner size="sm" animation="border" className="me-1" />Upload…</> : '➕ Ajouter un fichier'}
            </Button>
            <Button variant="secondary" onClick={() => setShowAttachmentModal(false)}>Fermer</Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* ════════════ Modal sessions par sport/mois ════════════ */}
      <Modal show={showMonthSportModal} onHide={() => setShowMonthSportModal(false)} size="xl" centered scrollable>
        <Modal.Header closeButton style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff' }}>
          <div>
            <Modal.Title className="fw-bold fs-5">📆 {monthSportTitle}</Modal.Title>
            <div style={{ fontSize: '0.82rem', opacity: 0.9, marginTop: 3 }}>
              {monthSportSessions.length} session{monthSportSessions.length > 1 ? 's' : ''}
              {' • '}
              {Object.values(monthSportPresences).reduce((acc, p) => acc + (p.members?.length || 0), 0)} présences membres
              {' • '}
              {Object.values(monthSportPresences).reduce((acc, p) => acc + (p.coaches?.length || 0), 0)} présences coachs
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="p-0">
          {monthSportLoading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="warning" /><p className="mt-3 text-muted">Chargement des présences…</p></div>
          ) : monthSportSessions.length === 0 ? (
            <div className="text-center py-5 text-muted"><div style={{ fontSize: '3rem' }}>📭</div><p className="mt-2">Aucune session trouvée pour ce sport ce mois-ci.</p></div>
          ) : (
            monthSportSessions.map((session, si) => {
              const pData = monthSportPresences[session.id] || { members: [], coaches: [] }
              const totalPresences = pData.members.length + pData.coaches.length
              const isEven = si % 2 === 0
              return (
                <div key={session.id} style={{ borderBottom: si < monthSportSessions.length - 1 ? '2px solid #fde68a' : 'none' }}>
                  <div className="d-flex align-items-center justify-content-between px-4 py-3 flex-wrap gap-2" style={{ background: isEven ? '#fffbeb' : '#fff' }}>
                    <div>
                      <span className="fw-bold text-capitalize" style={{ color: '#92400e', fontSize: '0.95rem' }}>📅 {new Date(session.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
                      {getSessionTimes(session) && <span className="ms-3 text-muted" style={{ fontSize: '0.82rem' }}>⏰ {getSessionTimeLabel(session).replace(' - ', ' – ')}</span>}
                      <span className="ms-3 text-muted" style={{ fontSize: '0.82rem' }}>📍 {getSessionLocation(session)}</span>
                    </div>
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                      <Badge bg="primary">{pData.members.length} membre{pData.members.length > 1 ? 's' : ''}</Badge>
                      <Badge bg="warning" text="dark">{pData.coaches.length} coach{pData.coaches.length > 1 ? 's' : ''}</Badge>
                      <Badge bg="success">{totalPresences} total</Badge>
                      <Button variant="outline-secondary" size="sm" style={{ fontSize: '0.72rem' }} onClick={() => { setShowMonthSportModal(false); openPresenceModal(session) }}>👁 Détail</Button>
                      <Button variant="outline-success" size="sm" style={{ fontSize: '0.72rem' }} onClick={() => downloadSessionData(session.id, getSessionTitle(session))}>📥 CSV</Button>
                    </div>
                  </div>
                  {pData.coaches.length > 0 && (
                    <div className="px-4 py-2" style={{ background: isEven ? '#fffbeb' : '#fff', borderTop: '1px solid #fde68a' }}>
                      <div className="fw-semibold mb-2" style={{ fontSize: '0.8rem', color: '#856404' }}>🏅 Coachs ({pData.coaches.length})</div>
                      <div className="d-flex flex-wrap gap-2">
                        {pData.coaches.map(p => (
                          <div key={p.id} className="border rounded px-2 py-1 bg-white d-flex align-items-center gap-2" style={{ fontSize: '0.78rem', borderColor: '#fde68a' }}>
                            <strong>{p.coaches?.last_name} {p.coaches?.first_name}</strong>
                            <Badge bg="warning" text="dark" style={{ fontSize: '0.6rem' }}>🕒 {formatDateTime(p.timestamp)}</Badge>
                            <Badge bg={p.mode === 'qr' ? 'success' : 'info'} style={{ fontSize: '0.6rem' }}>{p.mode === 'qr' ? '📱 QR' : '👤 Manuel'}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="px-4 pb-3 pt-2" style={{ background: isEven ? '#fffbeb' : '#fff', borderTop: '1px solid #fde68a' }}>
                    {pData.members.length === 0 ? (
                      <Alert variant="info" className="py-1 mb-0 small">Aucun membre présent pour cette session.</Alert>
                    ) : (
                      <>
                        <div className="fw-semibold mb-2" style={{ fontSize: '0.8rem', color: '#0d47a1' }}>👥 Membres présents ({pData.members.length})</div>
                        <Table responsive hover size="sm" className="mb-0" style={{ fontSize: '0.78rem' }}>
                          <thead className="table-light"><tr><th style={{ width: 32 }}>#</th><th>Nom</th><th>Prénom</th><th>Email</th><th>Téléphone</th><th className="text-center">Heure</th><th className="text-center">Mode</th></tr></thead>
                          <tbody>
                            {pData.members.map((p, i) => (
                              <tr key={p.id}>
                                <td className="text-muted">{i + 1}</td>
                                <td className="fw-bold">{p.participants?.last_name}</td>
                                <td>{p.participants?.first_name}</td>
                                <td><small className="text-muted">{p.participants?.email || '—'}</small></td>
                                <td><small className="text-muted">{p.participants?.phone || '—'}</small></td>
                                <td className="text-center"><Badge bg="success" style={{ fontSize: '0.62rem' }}>🕒 {formatDateTime(p.timestamp)}</Badge></td>
                                <td className="text-center"><Badge bg={p.mode === 'qr' ? 'success' : 'info'} style={{ fontSize: '0.62rem' }}>{p.mode === 'qr' ? '📱 QR' : '👤 Manuel'}</Badge></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <small className="text-muted">{monthSportSessions.length} session{monthSportSessions.length > 1 ? 's' : ''}{' • '}{Object.values(monthSportPresences).reduce((acc, p) => acc + (p.members?.length || 0), 0)} membres au total</small>
          <Button variant="secondary" onClick={() => setShowMonthSportModal(false)}>Fermer</Button>
        </Modal.Footer>
      </Modal>

    </ProtectedRoute>
  )
}

export default Reports